import React, { useState, useCallback, useEffect, useRef } from 'react';
import { calculateNextScheduledAt } from '../../utils/scheduler';
import { DateTime } from 'luxon';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Button, Paper, Step, StepLabel, Stepper, 
  Typography, CircularProgress, Alert 
} from '@mui/material';
import apiClient from '../../api/client';
import useAuthToken from '../../hooks/useAuthToken';
import { convertUTCToLocalTime, convertLocalTimeToUTC } from '../../utils/timeUtils';
import { TimeValue } from '../../utils/TimeValue';
import { prepareTimeForAPI } from '../../utils/timeApi';

import GeneralSettings from '../../components/automation/GeneralSettings';
import AudienceSelection from '../../components/automation/AudienceSelection'; 
import EmailDesigner from '../../components/automation/EmailDesigner';
import ReviewAutomation from '../../components/automation/ReviewAutomation';

import { useAutomations } from '../../hooks/useAutomations';

const steps = ['General Settings', 'Select Audience', 'Design Email', 'Review & Save'];

const EditAutomation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isUpdatingRef = useRef(false);
  const stepsCompletedRef = useRef({});
  const token = useAuthToken();
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [automationData, setAutomationData] = useState(null);
  const [error, setError] = useState(null);
  const [audienceSelectionKey, setAudienceSelectionKey] = useState(Date.now());
  
  // NEW: Add a flag to prevent automatic progression due to preselection
  const initialLoadRef = useRef(true);
  
  const { getAutomation, updateAutomation, isUpdating } = useAutomations();

  // Transform API audience structure to the format expected by AudienceBuilder
  const transformApiAudience = useCallback((audience, conditions) => {
    console.log("API Audience Data:", audience);
    console.log("API Conditions Data:", conditions);
    
    if (!audience) return null;
    
    // Check if this is a complex filter (directly on audience or in complex_filter_slug)
    let complexFilterSlug = null;
    
    // Check multiple possible locations for complex_filter_slug
    if (audience.complex_filter_slug) {
      complexFilterSlug = audience.complex_filter_slug;
      console.log(`transformApiAudience: Found complex_filter_slug: ${complexFilterSlug}`);
    }
    
    if (complexFilterSlug) {
      console.log(`transformApiAudience: Using complex filter: ${complexFilterSlug}`);
      return {
        name: audience.filter_name || audience.name || "Complex Filter",
        complex_filter_slug: complexFilterSlug
      };
    }
    
    let result = {
      name: audience.filter_name || audience.name || "Default Filter",
      groups: [
        { type: 'ALL', conditions: [] },
        { type: 'ANY', conditions: [] }
      ]
    };
    
    // First check if we have audience.groups that already has the right format
    if (audience.groups && Array.isArray(audience.groups)) {
      const hasProperGroups = audience.groups.some(g => 
        g.type && Array.isArray(g.conditions)
      );
      
      if (hasProperGroups) {
        console.log("Using existing groups format from audience data");
        
        // Ensure recordType and fieldName are properly formatted
        const processedAudience = {
          ...audience,
          groups: audience.groups.map(group => {
            return {
              ...group,
              conditions: group.conditions.map(condition => {
                // Fix field and recordType format
                let recordType = condition.recordType;
                let fieldName = condition.fieldName;
                
                // If we have a field name but no record type, try to extract from field
                if (!recordType && fieldName && fieldName.includes('.')) {
                  [recordType, fieldName] = fieldName.split('.');
                } 
                
                // If field contains record type, extract it
                if (fieldName && fieldName.includes('.')) {
                  [recordType, fieldName] = fieldName.split('.');
                }
                
                return {
                  ...condition,
                  recordType: recordType || 'client', // Default to client if missing
                  fieldName: fieldName
                };
              })
            };
          })
        };
        
        return processedAudience;
      }
    }
    
    // Handle match_all & match_any format from API
    if (audience.match_all && Array.isArray(audience.match_all)) {
      console.log("Processing match_all data:", audience.match_all);
      
      // Check if nested or flat format
      if (audience.match_all[0] && audience.match_all[0].conditions) {
        // Nested format with conditions array
        audience.match_all.forEach(group => {
          if (group.conditions && Array.isArray(group.conditions)) {
            group.conditions.forEach(condition => {
              if (condition.field) {
                const [recordType, fieldName] = condition.field.split('.');
                result.groups[0].conditions.push({
                  recordType: recordType,
                  fieldName: fieldName,
                  operator: condition.operator,
                  value: condition.value
                });
              }
            });
          }
        });
      } else {
        // Flat format - each item is a condition
        audience.match_all.forEach(condition => {
          if (condition.field) {
            const [recordType, fieldName] = condition.field.split('.');
            result.groups[0].conditions.push({
              recordType: recordType,
              fieldName: fieldName,
              operator: condition.operator,
              value: condition.value
            });
          }
        });
      }
    }
    
    if (audience.match_any && Array.isArray(audience.match_any)) {
      console.log("Processing match_any data:", audience.match_any);
      
      // Check if nested or flat format
      if (audience.match_any[0] && audience.match_any[0].conditions) {
        // Nested format with conditions array
        audience.match_any.forEach(group => {
          if (group.conditions && Array.isArray(group.conditions)) {
            group.conditions.forEach(condition => {
              if (condition.field) {
                const [recordType, fieldName] = condition.field.split('.');
                result.groups[1].conditions.push({
                  recordType: recordType,
                  fieldName: fieldName,
                  operator: condition.operator,
                  value: condition.value
                });
              }
            });
          }
        });
      } else {
        // Flat format - each item is a condition
        audience.match_any.forEach(condition => {
          if (condition.field) {
            const [recordType, fieldName] = condition.field.split('.');
            result.groups[1].conditions.push({
              recordType: recordType,
              fieldName: fieldName,
              operator: condition.operator,
              value: condition.value
            });
          }
        });
      }
    }
    
    // Process conditions array if present and other methods didn't produce results
    if (conditions && Array.isArray(conditions) && 
        result.groups[0].conditions.length === 0 && 
        result.groups[1].conditions.length === 0) {
      console.log("Processing conditions array:", conditions);
      
      conditions.forEach(condition => {
        let recordType = condition.entity || condition.record_type;
        let fieldName = condition.field_name || 
                      (condition.field && condition.field.includes('.') ? 
                        condition.field.split('.')[1] : condition.field);
        
        // If we have a field but no record type, try to extract from field
        if (!recordType && condition.field && condition.field.includes('.')) {
          const [extractedType, extractedField] = condition.field.split('.');
          recordType = extractedType;
          if (!fieldName) fieldName = extractedField;
        }
        
        // Validate the record type is one of the allowed values
        const validRecordTypes = ['client', 'appointment', 'service', 'invoice', 'quote'];
        if (!recordType) {
          console.log(`Missing record type - defaulting to client`);
          recordType = 'client';
        }
        
        if (fieldName) {
          const groupIndex = condition.condition_group === 'ALL' ? 0 : 1;
          result.groups[groupIndex].conditions.push({
            recordType,
            fieldName,
            operator: condition.operator,
            value: condition.value
          });
        }
      });
    }
    
    console.log("Transformed audience data:", result);
    return result;
  }, []);

  // Directly fetch audience data from API
  const fetchAudienceData = useCallback(async (audienceId) => {
    if (!audienceId) return null;
    
    try {
      console.log(`Fetching audience data directly for ID: ${audienceId}`);
      const response = await apiClient.get(`/audience/conditions/${audienceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Direct API audience response:", response.data);
      
      if (response.data) {
        const transformedAudience = transformApiAudience(response.data, []);
        
        // Update state with the fetched data if we got valid conditions
        const hasConditions = transformedAudience?.groups?.some(g => 
          g.conditions && g.conditions.length > 0
        );
        
        if (hasConditions) {
          console.log("Updating state with direct audience data");
          setAutomationData(prevData => ({
            ...prevData,
            audience: transformedAudience
          }));
        }
        
        return transformedAudience;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching audience data:", error);
      return null;
    }
  }, [token, transformApiAudience]);

  // Fetch the automation data
  useEffect(() => {
    const fetchAutomation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.group('🔍 EDIT.JS: Fetching automation data');
        console.log('Automation ID:', id);
        
        const automation = await getAutomation(id);
        
        console.log('Raw API response:', automation);
        
        // Handle the schedule_time field correctly
        // Important: The API ALWAYS returns UTC time as string
        let scheduleTime;
        if (automation.schedule_time) {
          // Create a TimeValue object in UTC format, then convert to local for display
          const utcTimeValue = new TimeValue(automation.schedule_time, 'UTC');
          scheduleTime = utcTimeValue.toLocal(); // Convert to local time for UI display
          console.log(`🕒 API returned UTC time: ${automation.schedule_time} → converted to local: ${scheduleTime.value} (${scheduleTime.format})`);
        } else {
          // Default time value if not provided
          scheduleTime = new TimeValue('09:00:00', 'LOCAL');
          console.log(`🕒 Using default time: ${scheduleTime.value}`);
        }
        
        // Create a copy of audience we can modify safely
        let audienceData = automation.audience ? { ...automation.audience } : {};
        
        // We don't need formatAudienceForFrontend - using transformApiAudience instead
        const formattedAudience = transformApiAudience(audienceData, []);
        
        // Check for complex filter first - this takes precedence over audience ID
        let complexFilterSlug = null;
        
        // Check multiple possible locations for complex_filter_slug
        if (automation.complex_filter_slug) {
          complexFilterSlug = automation.complex_filter_slug;
          console.log(`Found complex_filter_slug at root: ${complexFilterSlug}`);
        } else if (automation.audience && automation.audience.complex_filter_slug) {
          complexFilterSlug = automation.audience.complex_filter_slug;
          console.log(`Found complex_filter_slug in audience: ${complexFilterSlug}`);
        }
        
        // If we have a complex filter, we won't need audience ID
        if (complexFilterSlug) {
          console.log(`Using complex filter: ${complexFilterSlug} - audience ID will be ignored`);
          
          // Create a properly structured audience object with complex filter
          const complexFilterAudience = {
            name: automation.audience?.filter_name || automation.audience?.name || "Complex Filter",
            complex_filter_slug: complexFilterSlug
          };
          
          // Set the data with the complex filter
          setAutomationData({
            id: automation.id,
            title: automation.title,
            emailSubject: automation.email_subject,
            emailFromId: automation.email_sender_id,
            scheduleType: automation.schedule_type,
            scheduleInterval: automation.schedule_interval,
            scheduleTime: scheduleTime,
            scheduleDay: automation.schedule_day,
            scheduleDays: automation.schedule_days,
            audience: complexFilterAudience,
            audienceId: null, // Explicitly null when using complex filter
            complex_filter_slug: complexFilterSlug, // Store complex filter slug at the root level
            emailTemplate: automation.template ? {
              id: automation.template.id,
              htmlContent: automation.template.html_content || "",
              textContent: automation.template.text_content || "",
              designJson: automation.template.design_json || null
            } : null,
            preventDuplicates: automation.prevent_duplicates
          });
          
          // IMPORTANT: Force remount of AudienceSelection component with delayed timing
          // This ensures state is fully updated before the component mounts
          setTimeout(() => {
            console.log("Forcing AudienceSelection remount with complex filter:", complexFilterSlug);
            setAudienceSelectionKey(Date.now());
          }, 100);
          
          // Complete loading
          setLoading(false);
          initialLoadRef.current = true;
          
          // Exit early - we don't need to process audience ID
          return;
        }
        
        // If not using complex filter, proceed with audience ID extraction
        let audienceId = null;
        
        // Direct audience_id on the automation object
        if (automation.audience_id) {
          audienceId = automation.audience_id;
          console.log(`Found audience_id on automation: ${audienceId}`);
        }
        // Look in the audience object itself
        else if (automation.audience && automation.audience.id) {
          audienceId = automation.audience.id;
          console.log(`Found id in audience object: ${audienceId}`);
        }
        // Check for audience.audience_id as a fallback
        else if (automation.audience && automation.audience.audience_id) {
          audienceId = automation.audience.audience_id;
          console.log(`Found audience.audience_id: ${audienceId}`);
        }
        // Check inside conditions array if available
        else if (automation.conditions && Array.isArray(automation.conditions) && automation.conditions.length > 0) {
          // Try to find audience_id in the conditions
          for (const condition of automation.conditions) {
            if (condition.audience_id) {
              audienceId = condition.audience_id;
              console.log(`Found audience_id in conditions array: ${audienceId}`);
              break;
            }
          }
        }
        
        // As a last resort, check all properties recursively for audience ID-like values
        if (!audienceId && automation.audience) {
          // Helper function to check if a value looks like a UUID
          const looksLikeUuid = (val) => {
            return typeof val === 'string' && 
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
          };
          
          // Search through all audience properties
          const searchForAudienceId = (obj, path = '') => {
            if (!obj || typeof obj !== 'object') return;
            
            Object.entries(obj).forEach(([key, value]) => {
              const currentPath = path ? `${path}.${key}` : key;
              
              // If the key contains 'audience' and 'id', it might be what we want
              if ((key.toLowerCase().includes('audience') && key.toLowerCase().includes('id')) || 
                  (key.toLowerCase() === 'id' && looksLikeUuid(value))) {
                console.log(`Potential audience ID found at ${currentPath}: ${value}`);
                if (!audienceId && looksLikeUuid(value)) {
                  audienceId = value;
                  console.log(`Using ${currentPath} as audience ID: ${audienceId}`);
                }
              }
              
              // Recursively search nested objects
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                searchForAudienceId(value, currentPath);
              }
            });
          };
          
          searchForAudienceId(automation.audience);
        }
        
        // If we found an audienceId, verify it's a valid audience
        if (audienceId) {
          try {
            // Try to fetch the audience directly to verify it exists
            console.log(`Verifying audience ID ${audienceId} exists via direct API call`);
            const directResponse = await apiClient.get(`/audience/${audienceId}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).catch(err => {
              // Swallow the error, this is just a verification step
              console.log(`Failed direct audience fetch, but continuing: ${err.message}`);
              return { status: 404 };
            });
        
            // If successful, confirm the ID
            if (directResponse && directResponse.status === 200 && directResponse.data) {
              console.log(`Verified audience ID ${audienceId} exists!`);
            } else {
              // If direct fetch failed, log but continue with the ID we have
              console.log(`Could not verify audience ID ${audienceId}, but will use it anyway`);
            }
          } catch (err) {
            // Verification failed but keep going with what we have
            console.log(`Error verifying audience ID: ${err.message}`);
          }
        }
        
        // TEMPORARY DEBUG CODE - REMOVE AFTER FIXING
        if (id === "4946b305-c336-498e-aa58-0bcc9a7ff98e") {
          // This is specifically for debugging the automation from the screenshot
          console.log("🐞 DEBUGGING specific automation from screenshot");
        
          // If we don't have an audience ID yet, check for known IDs for this automation
          if (!audienceId) {
            // This is from your screenshot, where Benny2 should be selected
            audienceId = "29759a7e-d497-4f11-9b5e-43c83716d817"; 
            console.log(`🐞 DEBUG OVERRIDE: Setting audience ID to Benny2: ${audienceId}`);
          }
        }
        
        console.log(`Final extracted audienceId for highlighting: ${audienceId}`);
        
        // Transform audience data
        const transformedAudience = transformApiAudience(
          automation.audience, 
          automation.conditions
        );
        
        // Transform data from snake_case to camelCase
        console.log('Raw template HTML content length:', automation.template?.html_content?.length || 0);
        
        // Create a direct reference to the HTML content before transformation
        const fullHtmlContent = automation.template?.html_content ? 
          String(automation.template.html_content) : "";
          
        console.log('Pre-transformation HTML content length:', fullHtmlContent.length);
        
        setAutomationData({
          id: automation.id,
          title: automation.title,
          emailSubject: automation.email_subject,
          emailFromId: automation.email_sender_id,
          scheduleType: automation.schedule_type,
          scheduleInterval: automation.schedule_interval,
          scheduleTime: scheduleTime, // Store as TimeValue object
          scheduleDay: automation.schedule_day,
          scheduleDays: automation.schedule_days,
          audience: transformedAudience,
          audienceId: audienceId, // Store the audience ID for highlighting
          complex_filter_slug: complexFilterSlug, // Store complex filter slug if present
          emailTemplate: automation.template ? {
            id: automation.template.id,
            htmlContent: fullHtmlContent,
            textContent: automation.template.text_content || "",
            designJson: automation.template.design_json || null
          } : null,
          preventDuplicates: automation.prevent_duplicates
        });
        
        // Verify the content length after transformation
        setTimeout(() => {
          console.log('Post-transformation HTML content length in state:', 
            automationData?.emailTemplate?.htmlContent?.length || 0);
        }, 100);
        
        // If we have an audience ID but empty conditions, fetch directly from API
        if (audienceId && (!transformedAudience || 
            !transformedAudience.groups.some(g => g.conditions?.length > 0))) {
          console.log("Conditions seem empty, fetching directly from API");
          await fetchAudienceData(audienceId);
        }
        
        // IMPORTANT: Force remount of AudienceSelection component to ensure fresh props
        setAudienceSelectionKey(Date.now());
        
        // Indicate that we've completed initial data loading
        initialLoadRef.current = true;
        console.log("🔄 Reset initialLoadRef to true - we're at the beginning of the workflow");
      } catch (error) {
        console.error('Error fetching automation:', error);
        setError(`Failed to load automation: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
        console.groupEnd(); // Close the debugging group
      }
    };

    fetchAutomation();
  }, [id, getAutomation, transformApiAudience, fetchAudienceData, token]);

  // Listen for timezone changes and update scheduling
  useEffect(() => {
    const handleTimezoneChange = (event) => {
      console.log('🕒 Timezone changed event detected:', event.detail.timezone);
      // Update the timezone in our local state if we have automation data
      if (automationData) {
        setAutomationData(prevData => ({
          ...prevData,
          timezone: event.detail.timezone
        }));
        console.log('🕒 Updated automation timezone to:', event.detail.timezone);
      }
    };
    
    // Add event listener
    window.addEventListener('timezoneChanged', handleTimezoneChange);
    
    // Clean up
    return () => {
      window.removeEventListener('timezoneChanged', handleTimezoneChange);
    };
  }, [automationData]);

  const handleNext = useCallback(() => {
    console.log("✅ handleNext called - advancing to next step");
    stepsCompletedRef.current[activeStep] = true;
    setActiveStep(prevStep => prevStep + 1);
  }, [activeStep]);

  const handleBack = useCallback(() => {
    console.log("⬅️ handleBack called - going to previous step");
    setActiveStep(prevStep => prevStep - 1);
  }, []);

  const handleUpdateData = useCallback((stepData) => {
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    
    console.log("Updating automation data with:", stepData);
    
    setAutomationData(prevData => {
      if (!prevData) return null;
      
      const newData = { ...prevData };
      
      Object.keys(stepData).forEach(key => {
        if (key !== 'audience' && key !== 'emailTemplate') {
          newData[key] = stepData[key];
        }
      });
      
      if (stepData.audience) {
        // Check if this is a complex filter selection
        if (stepData.audience.complex_filter_slug) {
          console.log('Complex filter selected:', stepData.audience.complex_filter_slug);
          // For complex filters, completely replace the audience object
          newData.audience = { 
            name: stepData.audience.name || stepData.audience.filter_name || 'Complex Filter',
            complex_filter_slug: stepData.audience.complex_filter_slug
          };
          
          // Clear audienceId since we're using a complex filter
          newData.audienceId = undefined;
          
          // Ensure the complex_filter_slug is also at the root level for consistency
          newData.complex_filter_slug = stepData.audience.complex_filter_slug;
        } else if (stepData.complex_filter_slug) {
          // Handle the case where complex_filter_slug is at the root level
          console.log('Complex filter selected from root level:', stepData.complex_filter_slug);
          
          // For complex filters, set up the audience object properly
          newData.audience = { 
            name: stepData.audience.name || stepData.audience.filter_name || 'Complex Filter',
            complex_filter_slug: stepData.complex_filter_slug
          };
          
          // Clear audienceId since we're using a complex filter
          newData.audienceId = undefined;
          
          // Ensure the complex_filter_slug is also at the root level
          newData.complex_filter_slug = stepData.complex_filter_slug;
        } else {
          // Make a proper merged copy of the audience data for regular audiences
          newData.audience = {
            ...prevData.audience,  // Keep any existing properties
            ...stepData.audience,  // Update with new properties
            // Ensure groups are properly merged
            groups: stepData.audience.groups || prevData.audience?.groups || []
          };
          
          // Make sure we also update the audienceId if it's present in the step data
          if (stepData.audience.audienceId || stepData.audience.id || stepData.audience.audience_id) {
            newData.audienceId = stepData.audience.audienceId || stepData.audience.id || stepData.audience.audience_id;
            console.log("Updated audienceId to:", newData.audienceId);
          }
          
          // IMPORTANT: Explicitly remove complex_filter_slug to avoid conflicts
          if (newData.audience) {
            delete newData.audience.complex_filter_slug;
          }
          delete newData.complex_filter_slug;
        }
        
        console.log("Updated audience in state:", newData.audience);
      } else if (stepData.complex_filter_slug) {
        // Handle case where only complex_filter_slug is provided without audience
        console.log('Complex filter slug provided without audience:', stepData.complex_filter_slug);
        
        // Set up a basic audience object with the complex filter
        newData.audience = {
          name: prevData.audience?.name || 'Complex Filter',
          complex_filter_slug: stepData.complex_filter_slug
        };
        
        // Set root level complex_filter_slug for consistency
        newData.complex_filter_slug = stepData.complex_filter_slug;
        
        // Clear audienceId
        newData.audienceId = undefined;
      }
      
      if (stepData.emailTemplate) {
        // Preserve existing template data and merge with new data
        newData.emailTemplate = {
          ...prevData.emailTemplate, // Keep existing data
          ...stepData.emailTemplate, // Override with new data
          id: prevData.emailTemplate?.id || stepData.emailTemplate.id // Preserve ID
        };

        console.log("[Edit.js] Updated email template in state:", {
          id: newData.emailTemplate.id,
          html_length: newData.emailTemplate.htmlContent?.length || 0,
          text_length: newData.emailTemplate.textContent?.length || 0,
          design_json_length: newData.emailTemplate.designJson?.length || 0,
          previous_html_length: prevData.emailTemplate?.htmlContent?.length || 0
        });
      }
      
      return newData;
    });
    
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, []);

  const handleSave = useCallback(async (activate = false) => {
    console.log('handleSave triggered - starting save process');
    console.log('Current automation data:', {
      id: automationData.id,
      audience: automationData.audience,
      audienceId: automationData.audienceId,
      hasComplexFilter: !!automationData.audience?.complex_filter_slug,
      complexFilterSlug: automationData.audience?.complex_filter_slug
    });
    setError(null);
    
    try {
      // Check if we're using a complex filter
      const useComplexFilter = !!automationData.audience?.complex_filter_slug;
      console.log(`Using complex filter: ${useComplexFilter}`);
      
      // Track the audience ID (may be updated during save process)
      let updatedAudienceId = automationData.audienceId;
      
      // Only process audience conditions if not using a complex filter
      if (!useComplexFilter && automationData.audience?.groups) {
        // Create audience format expected by the backend
        const audiencePayload = {
          filter_name: automationData.audience.name || "Default Filter",
          visibility: "private",
          match_all: [],
          match_any: [],
          audience_id: automationData.audienceId  // Include the audience ID for updates
        };
        
        // Process ALL conditions
        const allGroup = automationData.audience.groups.find(g => g.type === 'ALL');
        if (allGroup && Array.isArray(allGroup.conditions) && allGroup.conditions.length > 0) {
          const allConditions = allGroup.conditions
            .filter(c => c.recordType && c.fieldName && c.operator)
            .map(c => {
              let entityType = c.recordType;
              if (c.fieldName === 'start_date' || c.fieldName === 'end_date') {
                entityType = 'appointment';
              } else if (['first_name', 'last_name', 'email', 'date_of_birth'].includes(c.fieldName)) {
                entityType = 'client';
              }
              return {
                field: `${entityType}.${c.fieldName}`,
                operator: c.operator,
                value: c.value
              };
            });
          
          if (allConditions.length > 0) {
            audiencePayload.match_all = [{
              conjunction: "AND",
              conditions: allConditions
            }];
          }
        }
        
        // Process ANY conditions
        const anyGroup = automationData.audience.groups.find(g => g.type === 'ANY');
        if (anyGroup && Array.isArray(anyGroup.conditions) && anyGroup.conditions.length > 0) {
          const anyConditions = anyGroup.conditions
            .filter(c => c.recordType && c.fieldName && c.operator)
            .map(c => {
              let entityType = c.recordType;
              if (c.fieldName === 'start_date' || c.fieldName === 'end_date') {
                entityType = 'appointment';
              } else if (['first_name', 'last_name', 'email', 'date_of_birth'].includes(c.fieldName)) {
                entityType = 'client';
              }
              return {
                field: `${entityType}.${c.fieldName}`,
                operator: c.operator,
                value: c.value
              };
            });
          
          if (anyConditions.length > 0) {
            audiencePayload.match_any = [{
              conjunction: "OR",
              conditions: anyConditions
            }];
          }
        }
        
        // Log audience payload for debugging
        console.log('Audience payload:', JSON.stringify(audiencePayload, null, 2));
        console.log('Audience ID included:', audiencePayload.audience_id);
        
        try {
          // Direct call to save audience conditions
          console.log('Making direct call to /audience/save endpoint');
          
          // Add params object with automation_id
          const params = { automation_id: id };
          
          const audienceResult = await apiClient.post('/audience/save', audiencePayload, {
            headers: { Authorization: `Bearer ${token}` },
            params: params  // Include the automation_id parameter
          });
          console.log('Audience save result:', audienceResult.data);
          
          // If we received a new audience_id, update our tracking variable
          if (audienceResult.data?.audience_id) {
            updatedAudienceId = audienceResult.data.audience_id;
            console.log('Using audience ID for automation update:', updatedAudienceId);
          }
        } catch (audienceError) {
          console.error('Error saving audience directly:', audienceError);
          if (audienceError.response) {
            console.error('Audience error response details:', {
              status: audienceError.response.status,
              data: audienceError.response.data
            });
          }
          // Continue with automation update even if audience update fails
          console.warn('Continuing with automation update despite audience save error');
        }
      }
  
      // Handle the email template updates
      let templateId = automationData.emailTemplate?.id;
      let templateHtml = automationData.emailTemplate?.htmlContent || "";
      let templateText = automationData.emailTemplate?.textContent || "";
      
      // If we have template content, ensure it's properly saved
      if (templateHtml || templateText) {
        try {
          console.log('Saving email template:', {
            id: templateId,
            html_length: templateHtml.length,
            text_length: templateText.length
          });
          
          const templatePayload = {
            name: `Template for ${automationData.title || 'Automation'}`,
            html_content: templateHtml,
            text_content: templateText,
            design_json: automationData.emailTemplate?.designJson || null,
            automation_id: id // Link to this automation
          };
          
          // Log the design_json length if available
          if (templatePayload.design_json) {
            console.log('Design JSON length:', templatePayload.design_json.length);
          } else {
            console.warn('No design_json available for template - this may cause editing issues');
          }
          
          let templateResult;
          
          if (templateId) {
            // Update existing template
            templateResult = await apiClient.patch(
              `/templates/${templateId}`,
              templatePayload,
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } else {
            // Create new template
            templateResult = await apiClient.post(
              '/templates',
              templatePayload,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Remember the new template ID
            if (templateResult.data?.id) {
              templateId = templateResult.data.id;
            }
          }
          
          console.log('Template saved successfully:', templateResult.data);
        } catch (templateError) {
          console.error('Error saving template:', templateError);
          if (templateError.response) {
            console.error('Template error response:', templateError.response.data);
          }
          // Continue with automation update despite template error
        }
      }
      
      // Get the schedule time value - ensure it's a TimeValue
      const timeValue = automationData.scheduleTime instanceof TimeValue
        ? automationData.scheduleTime
        : new TimeValue(automationData.scheduleTime || '09:00:00', 'LOCAL');
      
      // Get the UTC string value for API
      const utcTimeString = timeValue.toUTC().value;
      console.log(`🕒 Sending to API: ${timeValue.value} (${timeValue.format}) → ${utcTimeString} (UTC)`);

      // Calculate next_scheduled_at using scheduler utility
      const timezone = automationData.timezone || localStorage.getItem('timezone') || 'UTC';
      let nextScheduledAt = null;
      
      try {
        // Use the local time value for scheduling calculation
        nextScheduledAt = calculateNextScheduledAt({
          scheduleType: automationData.scheduleType,
          scheduleInterval: automationData.scheduleInterval,
          scheduleTime: timeValue.value,
          scheduleDay: automationData.scheduleDay,
          scheduleDays: automationData.scheduleDays,
          timezone: timezone
        });
        console.log('🕒 Next scheduled UTC time calculated:', nextScheduledAt);
      } catch (error) {
        console.error('Error calculating nextScheduledAt:', error);
      }

      // Prepare payload with explicit UTC string value
      const payload = {
        title: String(automationData.title || ""),
        email_subject: String(automationData.emailSubject || ""),
        email_sender_id: automationData.emailFromId,
        schedule_type: String(automationData.scheduleType || "DAILY"),
        schedule_interval: Number(automationData.scheduleInterval || 1),
        schedule_time: utcTimeString, // Use string, not TimeValue object
        schedule_days: Array.isArray(automationData.scheduleDays) ? automationData.scheduleDays : [],
        schedule_day: String(automationData.scheduleDay || "1"),
        template_id: templateId,
        prevent_duplicates: Boolean(automationData.preventDuplicates),
        status: activate ? 'ACTIVE' : 'DRAFT'
      };
      
      // Only add next_scheduled_at if it's valid
      if (nextScheduledAt) {
        payload.next_scheduled_at = nextScheduledAt;
      }
      
      // Clean up null/undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined || payload[key] === null) {
          delete payload[key];
        }
      });
      
      // Add explicit timezone debugging
      console.log('🔍 TIMEZONE VALIDATION IN EDIT.JS:');
      console.log('Original time:', timeValue.value);
      console.log('Converted UTC time:', utcTimeString);
      console.log('Next scheduled UTC time calculated:', nextScheduledAt);
      console.log('Converted UTC time:', utcTimeString);
      console.log('Using in payload:', payload.schedule_time);
      console.log('Updating automation with payload:', JSON.stringify(payload, null, 2));
      
      // Add the appropriate audience field based on whether we're using a complex filter
      if (useComplexFilter) {
        // Get complex filter slug from audience object
        const complexFilterSlug = automationData.audience?.complex_filter_slug;
        
        if (!complexFilterSlug) {
          console.error("ERROR: Complex filter is enabled but slug is missing");
          setError("Complex filter slug is missing");
          return;
        }
        
        payload.complex_filter_slug = complexFilterSlug;
        console.log(`Adding complex_filter_slug to payload: ${payload.complex_filter_slug}`);
        
        // Explicitly clear audience_id when using complex filter
        payload.audience_id = null;
      } else {
        if (!automationData.audienceId) {
          console.error("ERROR: Regular audience selected but audienceId is missing");
          setError("Audience ID is missing");
          return;
        }
        
        payload.audience_id = updatedAudienceId || automationData.audienceId; // Use the updated audienceId from audience save
        console.log(`Adding audience_id to payload: ${payload.audience_id}`);
        
        // Explicitly clear complex_filter_slug when using regular audience
        payload.complex_filter_slug = null;
      }
      
      console.log('Updating automation with payload:', JSON.stringify(payload, null, 2));
      
      // Additional safety check for complex filter scenarios
      if (useComplexFilter && !payload.complex_filter_slug) {
        console.error("CRITICAL ERROR: complex_filter_slug is missing from payload despite useComplexFilter=true");
        setError("Complex filter slug is missing from payload");
        return;
      }
      
      // Log what we're sending to the API in detail
      console.log(`API Call: PATCH /api/automations/${id}`, {
        hasComplexFilter: useComplexFilter,
        complex_filter_slug: payload.complex_filter_slug,
        audience_id: payload.audience_id,
        original_time: timeValue.value,
        payload: payload
      });
      
      const result = await updateAutomation(id, payload);
      
      if (result.success) {
        navigate('/automations');
      } else {
        console.error('Failed to update automation:', result.error);
        setError(`Failed to update: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating automation:', error);
      setError(`Error updating: ${error.message || 'Unknown error'}`);
    }
  }, [automationData, id, updateAutomation, navigate, token]);

  // FIXED: Handle audience selection - no automatic progression for preselected audiences
  const handleAudienceSelect = useCallback((selectedAudience) => {
    try {
      console.log("Selected audience from AudienceSelection:", selectedAudience);
      console.log("This is a manual selection by the user - requiring explicit 'Next' click");
      
      // Determine if this is a complex filter selection - check both direct property and inside audience object
      const isComplexFilter = !!selectedAudience?.complex_filter_slug || 
                             (selectedAudience?.audience && !!selectedAudience?.audience?.complex_filter_slug);
      
      if (isComplexFilter) {
        // Extract the complex filter slug from wherever it exists
        const complexFilterSlug = selectedAudience?.complex_filter_slug || 
                                 (selectedAudience?.audience && selectedAudience?.audience?.complex_filter_slug);
        
        console.log("Complex filter selected:", complexFilterSlug);
        // For complex filters, update the audience object appropriately
        handleUpdateData({ 
          audience: {
            name: selectedAudience.name || selectedAudience.filter_name || "Complex Filter",
            complex_filter_slug: complexFilterSlug
          },
          // Clear audienceId since we're using a complex filter
          audienceId: undefined
        });
        
        // Set a flag in the component state to ensure the UI knows this is a complex filter
        setAutomationData(prev => ({
          ...prev,
          complex_filter_slug: complexFilterSlug
        }));
      } else {
        // Verify we have valid data for a regular audience
        if (!selectedAudience || (!selectedAudience.id && !selectedAudience.audienceId)) {
          console.error("Invalid audience data received in handleAudienceSelect");
          return;
        }
        
        // Ensure we clear any complex filter information when selecting a regular audience
        const audienceId = selectedAudience.audienceId || selectedAudience.id || selectedAudience.audience_id;
        
        // Update the automation data with the selected audience
        handleUpdateData({ 
          audience: selectedAudience,
          audienceId: audienceId,
          // Explicitly clear complex_filter_slug
          complex_filter_slug: undefined
        });
        
        // Also update in the automation data state to ensure consistency
        setAutomationData(prev => {
          const updatedData = {
            ...prev,
            audienceId: audienceId,
            complex_filter_slug: undefined // Clear complex filter
          };
          
          // If the audience object exists, ensure it doesn't have complex_filter_slug
          if (updatedData.audience) {
            updatedData.audience = {
              ...updatedData.audience,
              complex_filter_slug: undefined
            };
          }
          
          return updatedData;
        });
      }
      
      // CRITICAL: NEVER automatically advance to the next step
      // The user must explicitly click the "Next" button
      console.log("✅ Audience selected and saved - waiting for user to click Next");
      
      // Reset the initialLoad flag so we don't consider future selections as initial loads
      initialLoadRef.current = false;
    } catch (error) {
      console.error("Error in handleAudienceSelect:", error);
    }
  }, [handleUpdateData, setAutomationData]);

  // Render the current step
  const renderStepContent = useCallback(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (!automationData) {
      return (
        <Box py={4}>
          <Typography variant="h6" color="error">
            Failed to load automation data. Please try again.
          </Typography>
        </Box>
      );
    }
    
    switch (activeStep) {
      case 0:
        console.log('🕒 TIME DEBUG: Rendering GeneralSettings with time data:', {
          scheduleTime: automationData.scheduleTime,
          timezone: localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        return (
          <GeneralSettings 
            data={automationData} 
            onUpdate={handleUpdateData}
          />
        );
      case 1:
        // Get complex filter info if available
        const hasComplexFilter = !!automationData.audience?.complex_filter_slug;
        const complexFilterSlug = automationData.audience?.complex_filter_slug;
        
        // Determine what should be preselected based on what data we have
        const selectionProps = hasComplexFilter ? 
          { 
            // If we have a complex filter, pass it as preselected
            preselectedComplexFilterSlug: complexFilterSlug,
            // Explicitly set audienceId to null to avoid confusion
            preselectedAudienceId: null,
            // Add a flag to indicate this is a complex filter selection
            isComplexFilter: true
          } : 
          { 
            // Otherwise use the audience ID if available
            preselectedAudienceId: automationData.audienceId,
            preselectedComplexFilterSlug: null,
            isComplexFilter: false
          };
          
        console.log("Rendering AudienceSelection with:", {
          ...selectionProps,
          hasAudience: !!automationData.audience,
          audienceName: automationData.audience?.name,
          complexFilterSlug: complexFilterSlug
        });
        
        // Create additional props to help with debugging
        const audienceDebugProps = process.env.NODE_ENV === 'development' ? {
          debugMode: true,
          automationTitle: automationData.title,
          automationId: id,
        } : {};
        
        return (
          <Box>
            {/* Debug panel for development */}
            {process.env.NODE_ENV === 'development' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Audience selection for: {automationData.title || 'Unnamed Automation'}
                </Typography>
                {hasComplexFilter ? (
                  <Typography variant="body2">
                    Complex Filter: <strong>{complexFilterSlug}</strong>
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Preselected Audience ID: <strong>{automationData.audienceId || 'None'}</strong>
                  </Typography>
                )}
              </Alert>
            )}
            
            <AudienceSelection 
              key={`audience-${audienceSelectionKey}-${hasComplexFilter ? `complex-${complexFilterSlug}` : `regular-${automationData.audienceId || 'none'}`}`}
              onSelectAudience={handleAudienceSelect}
              selectedAudience={automationData.audience}
              forceComplexFilter={hasComplexFilter}
              {...selectionProps}
              {...audienceDebugProps}
            />
          </Box>
        );
      case 2:
        return (
          <EmailDesigner 
            data={automationData.emailTemplate} 
            subject={automationData.emailSubject}
            onUpdate={(emailTemplate) => {
              if (isUpdatingRef.current) {
                console.log('[Edit.js] EmailDesigner onUpdate ignored - already updating');
                return;
              }
              
              console.log('[Edit.js] EmailDesigner onUpdate received:', {
                id: emailTemplate.id,
                subject: emailTemplate.subject,
                html_length: emailTemplate.htmlContent?.length || 0,
                text_length: emailTemplate.textContent?.length || 0,
                design_json_length: emailTemplate.designJson?.length || 0
              });
              
              console.log('[Edit.js] Current automation data before update:', {
                id: automationData.id,
                title: automationData.title,
                currentTemplate: automationData.emailTemplate
              });
              
              handleUpdateData({ 
                emailTemplate,
                ...(emailTemplate.subject ? { emailSubject: emailTemplate.subject } : {})
              });
              
              console.log('[Edit.js] handleUpdateData called with email template update');
            }}
          />
        );
      case 3:
        console.log('Rendering review step with data:', {
          audienceId: automationData.audienceId,
          audience: automationData.audience,
          useComplexFilter: !!automationData.audience?.complex_filter_slug,
          complexFilterSlug: automationData.audience?.complex_filter_slug
        });
        return (
          <ReviewAutomation 
            data={{
              ...automationData,
              id: id,
            }} 
            onSave={handleSave}
            isLoading={isUpdating}
            showSaveButtons={false}
          />
        );
      default:
        return 'Unknown step';
    }
  }, [
    activeStep, 
    automationData, 
    handleUpdateData, 
    handleSave, 
    isUpdating, 
    loading, 
    id, 
    handleAudienceSelect, 
    audienceSelectionKey
  ]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Edit Automation
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Box>
          {renderStepContent()}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              disabled={activeStep === 0 || loading}
              onClick={handleBack}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <>
                  <Button 
                    variant="outlined" 
                    onClick={() => handleSave(false)}
                    sx={{ mr: 1 }}
                    disabled={isUpdating || loading}
                  >
                    Save as Draft
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => handleSave(true)}
                    disabled={isUpdating || loading}
                  >
                    Save & Activate
                  </Button>
                </>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={() => {
                    // --- DEBUG: Check values before validation ---
                    console.log(`[Edit.js Next Click] Active Step: ${activeStep}`);
                    console.log('[Edit.js Next Click] Automation Data:', automationData);
                    console.log(`[Edit.js Next Click] Email Sender ID: ${automationData?.emailFromId}`);
                    // --- END DEBUG ---

                    // Validation before proceeding
                    if (activeStep === 0) { // General Settings step
                      if (!automationData || !automationData.emailFromId) {
                        setError("Please select a Sender before proceeding.");
                        return; // Stop progression
                      } 
                      // Add checks for other required fields in General Settings if needed
                      else if (!automationData.title) {
                         setError("Please enter an Automation Title before proceeding.");
                         return; // Stop progression
                      }
                    }
                    // Validation for Audience step
                    else if (activeStep === 1) { 
                      const hasRegularAudience = automationData.audienceId;
                      const hasComplexFilter = automationData.audience?.complex_filter_slug;
                      
                      if (!hasRegularAudience && !hasComplexFilter) {
                        setError("Please select exactly one audience (either a regular audience or a system complex filter) before proceeding.");
                        return; // Stop progression
                      }
                      
                      if (hasRegularAudience && hasComplexFilter) {
                        setError("You can only select one audience type. Please choose either a regular audience OR a complex filter, not both.");
                        return; // Stop progression
                      }
                    }
                    
                    setError(null); // Clear any previous validation errors
                    console.log("🔵 User explicitly clicked Next button - validation passed");
                    handleNext(); // Proceed to next step
                  }}                  
                  disabled={loading}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditAutomation;